import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// Auth / Payment
import Login from "./pages/Login";
import Register from "./pages/Register";
import Subscribe from "./pages/Subscribe";
import PaymentSuccess from "./pages/PaymentSuccess";

// ✅ NEW: Security Question / Forgot Password
import SetupSecurityQuestion from "./pages/SetupSecurityQuestion";
import ForgotPassword from "./pages/ForgotPassword";

// Core pages / components
import Landing from "./pages/Landing";
import Join from "./pages/JoinCase";
import AdminDashboard from "./pages/AdminDashboard";

import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import Clients from "./components/Clients";
import ClientDetails from "./components/ClientDetails";
import Cases from "./components/Cases";
import CaseDetails from "./components/CaseDetails";
import Schedule from "./components/Schedule";
import Analytics from "./components/Analytics";

import AISection from "./components/AISection";
import AIChatPage from "./pages/AIChatPage";

import LegalDocs from "./components/LegalDocs";
import LegalDocForm from "./components/LegalDocForm";

import Invite from "./pages/Invite";
import ContactOwner from "./components/ContactOwner";
import SendNotification from "./pages/SendNotification";
import NotificationsPage from "./pages/NotificationsPage";

import TemplatesPage from "./pages/TemplatesPage";
import TemplatesManager from "./pages/TemplatesManager";
import TemplateForm from "./pages/TemplateForm";
import CreateTemplate from "./pages/CreateTemplate";

import FileManager from "./pages/FileManager";

/* ===========================================================
 🧠 Roles (ثوابت عشان مايتكرروش)
=========================================================== */
const ROLES = {
  ADMIN: "Admin",
  MAIN_LAWYER: "محامي رئيسي",
};

/* ===========================================================
 🧭 Layout للوحة المحامي
=========================================================== */
function DashboardLayout({ children, setToken }) {
  return (
    <div className="flex w-full bg-gray-900 text-white min-h-screen">
      <Sidebar setToken={setToken} />
      <main className="flex-1 p-6 overflow-y-auto">{children}</main>
    </div>
  );
}

/* ===========================================================
 🔒 PrivateRoute
  - يمنع الدخول بدون token
  - يمنع الدخول لو role مش ضمن allowed
  - لو allowed مش موجودة يبقى يكفي token فقط
=========================================================== */
function PrivateRoute({ children, allowed }) {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role") || "";

  if (!token) return <Navigate to="/login" replace />;

  if (Array.isArray(allowed) && allowed.length > 0 && !allowed.includes(role)) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

/* ===========================================================
 🚀 App
=========================================================== */
export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("token"));

  useEffect(() => {
    const handleStorage = () => setToken(localStorage.getItem("token"));
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <Routes>
      {/* 🌍 Landing */}
      <Route path="/" element={<Landing />} />

      {/* 🔐 Auth */}
      <Route path="/login" element={<Login setToken={setToken} />} />
      <Route path="/register" element={<Register />} />
      <Route path="/join" element={<Join />} />

      {/* ✅ Security Question / Forgot Password */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route
        path="/setup-security-question"
        element={
          <PrivateRoute>
            <SetupSecurityQuestion />
          </PrivateRoute>
        }
      />

      {/* 💳 Payment (Public) */}
      <Route path="/subscribe" element={<Subscribe />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />

      {/* 👑 Admin */}
      <Route
        path="/admin"
        element={
          <PrivateRoute allowed={[ROLES.ADMIN]}>
            <AdminDashboard />
          </PrivateRoute>
        }
      />

      {/* ⚖️ Lawyer Dashboard */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <Dashboard />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/clients"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <Clients />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/clients/:id"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <ClientDetails />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/cases"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <Cases />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/cases/:id"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <CaseDetails />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/schedule"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <Schedule />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/analytics"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <Analytics />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      {/* 🔗 Invite */}
      <Route
        path="/invite"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <Invite />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      {/* 📜 Legal Docs */}
      <Route
        path="/legal-docs"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <LegalDocs />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/legal-docs/:type"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <LegalDocForm />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      {/* 🧾 Templates */}
      <Route
        path="/templates"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <TemplatesPage />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/templates-manager"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <TemplatesManager />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/generate/:id"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <TemplateForm />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/create-template"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <CreateTemplate />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      {/* 🤖 AI */}
      <Route
        path="/ai"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <AISection />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      <Route
        path="/ai/:tool"
        element={
          <PrivateRoute allowed={[ROLES.MAIN_LAWYER]}>
            <DashboardLayout setToken={setToken}>
              <AIChatPage />
            </DashboardLayout>
          </PrivateRoute>
        }
      />

      {/* 🔔 Notifications / Contact */}
      <Route path="/contact-owner" element={<ContactOwner />} />
      <Route path="/send-notification" element={<SendNotification />} />
      <Route path="/notifications" element={<NotificationsPage />} />

      {/* 📁 File Manager */}
      <Route path="/file-manager" element={<FileManager />} />

      {/* 🚫 Not Found */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
